// @spec GHG-STR-043
/**
 * TypeScript types for GHG AI classification
 */

/**
 * Classification data source types
 */
export type ClassificationSource = 'ocr' | 'bulk_import' | 'integration' | 'manual';

/**
 * Classification status
 */
export type ClassificationStatus = 'pending' | 'completed' | 'confirmed' | 'failed';

/**
 * Confidence level for classification suggestions
 */
export type ConfidenceLevel = 'high' | 'medium' | 'low';

/**
 * Extracted fields from input data
 */
export interface ExtractedFields {
  supplier?: string | null;
  consumption?: number | null;
  consumptionUnit?: string | null;
  billingPeriodStart?: string | null;
  billingPeriodEnd?: string | null;
  fuelType?: string | null;
  totalAmount?: number | null;
  currency?: string | null;
}

/**
 * Input data for classification
 */
export interface ClassificationInputData {
  documentType?: 'electricity_bill' | 'gas_bill' | 'fuel_receipt' | 'water_bill' | null;
  extractedText?: string | null;
  extractedFields?: ExtractedFields | null;
  fileName?: string | null;
  rawContent?: string | null;
}

/**
 * Organization context for classification
 */
export interface OrganizationContext {
  facilityId?: string | null;
  reportingPeriodId?: string | null;
}

/**
 * Request to classify a single data item
 */
export interface ClassifyRequest {
  source: ClassificationSource;
  sourceId?: string | null;
  data: ClassificationInputData;
  organizationContext?: OrganizationContext | null;
}

/**
 * A single suggestion field with confidence
 */
export interface SuggestionField<T = unknown> {
  value: T;
  confidence: number;
  reasoning?: string | null;
  alternatives?: { value: T; confidence: number }[] | null;
  isOriginal?: boolean | null;
}

/**
 * Suggested emission factor from Climatiq
 */
export interface EmissionFactorSuggestion {
  climatiqId: string;
  name: string;
  source: string;
  region?: string | null;
  co2ePerUnit: number;
  unit: string;
  confidence: number;
}

/**
 * AI-generated classification suggestions
 */
export interface ClassificationSuggestions {
  scope: SuggestionField<1 | 2 | 3>;
  scope3Category?: SuggestionField<string> | null;
  sourceCategory: SuggestionField<string>;
  activityType: SuggestionField<string>;
  unit: SuggestionField<string>;
  suggestedDatapointCode: SuggestionField<string>;
  suggestedEmissionFactors: EmissionFactorSuggestion[];
}

/**
 * Response from single item classification
 */
export interface ClassifyResponse {
  classificationId: string;
  suggestions: ClassificationSuggestions;
  overallConfidence: number;
  requiresReview: boolean;
  createdAt: string;
}

/**
 * Single item in batch classification request
 */
export interface BatchClassifyItem {
  rowIndex: number;
  data: ClassificationInputData;
}

/**
 * Request to classify multiple data items
 */
export interface BatchClassifyRequest {
  source: ClassificationSource;
  items: BatchClassifyItem[];
  organizationContext?: OrganizationContext | null;
}

/**
 * Single result item in batch classification response
 */
export interface BatchClassifyResultItem {
  rowIndex: number;
  classificationId: string;
  suggestions: ClassificationSuggestions;
  overallConfidence: number;
}

/**
 * Summary statistics for batch classification
 */
export interface BatchClassifySummary {
  total: number;
  highConfidence: number;
  mediumConfidence: number;
  lowConfidence: number;
}

/**
 * Response from batch classification
 */
export interface BatchClassifyResponse {
  batchId: string;
  status: 'completed' | 'processing';
  results: BatchClassifyResultItem[];
  summary: BatchClassifySummary;
}

/**
 * User corrections to AI classification
 */
export interface ClassificationCorrections {
  scope?: 1 | 2 | 3 | null;
  scope3Category?: string | null;
  sourceCategory?: string | null;
  activityType?: string | null;
  unit?: string | null;
  datapointCode?: string | null;
}

/**
 * Request to confirm or correct AI classification
 */
export interface ConfirmClassificationRequest {
  accepted: boolean;
  corrections?: ClassificationCorrections | null;
  selectedEmissionFactorId?: string | null;
  notes?: string | null;
}

/**
 * Final confirmed classification
 */
export interface FinalClassification {
  scope: number;
  scope3Category?: string | null;
  sourceCategory: string;
  activityType: string;
  unit: string;
  datapointCode?: string | null;
  emissionFactorId?: string | null;
}

/**
 * Response after confirming classification
 */
export interface ConfirmClassificationResponse {
  classificationId: string;
  status: 'confirmed';
  finalClassification: FinalClassification;
  wasCorrection: boolean;
  confirmedAt: string;
  confirmedBy: string;
}

/**
 * Response for getting classification status
 */
export interface GetClassificationResponse {
  classificationId: string;
  status: ClassificationStatus;
  suggestions?: ClassificationSuggestions | null;
  finalClassification?: FinalClassification | null;
  createdAt: string;
}

/**
 * Confidence thresholds for classification
 */
export const CLASSIFICATION_CONFIDENCE_THRESHOLDS = {
  HIGH: 0.85,
  MEDIUM: 0.6,
} as const;

/**
 * Get confidence level from a score
 */
export function getClassificationConfidenceLevel(confidence: number): ConfidenceLevel {
  if (confidence >= CLASSIFICATION_CONFIDENCE_THRESHOLDS.HIGH) return 'high';
  if (confidence >= CLASSIFICATION_CONFIDENCE_THRESHOLDS.MEDIUM) return 'medium';
  return 'low';
}

/**
 * Get display text for scope
 */
export function getScopeDisplayText(scope: number): string {
  switch (scope) {
    case 1:
      return 'Scope 1 (Direct)';
    case 2:
      return 'Scope 2 (Indirect - Energy)';
    case 3:
      return 'Scope 3 (Value Chain)';
    default:
      return `Scope ${String(scope)}`;
  }
}

/**
 * Format source category for display
 */
export function formatSourceCategory(category: string): string {
  return category
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// =========================================================================
// GHG-STR-070: Classification-Assisted Mapping types
// =========================================================================

/**
 * Request to classify free-text into Climatiq activities
 */
export interface ClassificationSuggestRequest {
  text: string;
  sourceContext?: string | null;
}

/**
 * A single Climatiq activity suggestion
 */
export interface ClimatiqActivitySuggestion {
  activityId: string;
  name: string;
  category: string;
  sector: string;
  source: string;
  region?: string | null;
  year?: number | null;
  unit: string;
  unitType: string;
  confidence: number;
}

/**
 * Response from free-text classification
 */
export interface ClassificationSuggestResponse {
  inputText: string;
  suggestions: ClimatiqActivitySuggestion[];
  requiresReview: boolean;
  autoAccepted: boolean;
  mappingMemoryId?: string | null;
  confidenceThreshold: number;
}

/**
 * Request to approve a classification mapping
 */
export interface ApproveMappingRequest {
  inputText: string;
  activityId?: string | null;
  sourceCategory: string;
  activityType: string;
  scope: 1 | 2 | 3;
  scope3Category?: string | null;
  unit: string;
  datapointCode?: string | null;
  confidence: number;
  wasOverride: boolean;
  sourceContext?: string | null;
  notes?: string | null;
}

/**
 * Response after approving a mapping
 */
export interface ApproveMappingResponse {
  id: string;
  inputText: string;
  mappedSourceCategory: string;
  mappedActivityType: string;
  createdAt: string;
}

/**
 * A mapping memory entry
 */
export interface MappingMemoryItem {
  id: string;
  inputText: string;
  mappedActivityId?: string | null;
  mappedSourceCategory: string;
  mappedActivityType: string;
  mappedScope: number;
  mappedScope3Category?: string | null;
  mappedUnit: string;
  mappedDatapointCode?: string | null;
  confidenceAtMapping: number;
  wasOverride: boolean;
  hitCount: number;
  lastUsedAt?: string | null;
  approvedBy?: string | null;
  approvedAt?: string | null;
  notes?: string | null;
  isActive: boolean;
  createdAt: string;
}

/**
 * Response with list of mapping memory entries
 */
export interface MappingMemoryListResponse {
  items: MappingMemoryItem[];
  total: number;
}

/**
 * Request to update a mapping
 */
export interface UpdateMappingRequest {
  isActive?: boolean;
  notes?: string;
}
